import { useEffect, useState } from "react";
import "@/App.css";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { initSmoothScroll, scrollToSection } from "@/lib/smooth";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import About from "@/components/About";
import Products from "@/components/Products";
import Brands from "@/components/Brands";
import WhyUs from "@/components/WhyUs";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import CategoryPage from "@/components/CategoryPage";
import WhatsAppButton from "@/components/WhatsAppButton";
import Admin from "@/pages/Admin";

const Intro = () => (
  <motion.div
    data-testid="intro-overlay"
    exit={{ y: "-100%" }}
    transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
    className="fixed inset-0 z-[100] flex items-center justify-center bg-parchment"
  >
    <div className="px-6 text-center">
      <motion.p
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="font-accent text-[11px] font-semibold tracking-[0.5em] text-gold"
      >
        RIGHT SOURCING · FAIR TRADING
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto mt-6 w-fit"
      >
        <img src="/logo-full.png" alt="Praveen Textiles" className="h-32 w-auto sm:h-44" />
      </motion.div>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.7, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto mt-7 h-px w-44 bg-gold/60"
      />
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.05, duration: 0.8 }}
        className="mt-5 font-mono text-[10px] uppercase tracking-[0.4em] text-clay"
      >
        Since 1988 · Chennai
      </motion.p>
    </div>
  </motion.div>
);

const Landing = () => (
  <>
    <Header />
    <main>
      <Hero />
      <About />
      <Products />
      <Brands />
      <WhyUs />
      <Contact />
    </main>
    <Footer />
  </>
);

function App() {
  const isLanding = window.location.pathname === "/";
  const [showIntro, setShowIntro] = useState(isLanding);

  useEffect(() => {
    initSmoothScroll();
    if (!isLanding) return undefined;
    const t = setTimeout(() => setShowIntro(false), 2100);
    return () => clearTimeout(t);
  }, [isLanding]);

  useEffect(() => {
    if (!isLanding || !window.location.hash) return undefined;
    const t = setTimeout(() => scrollToSection(window.location.hash), 3600);
    return () => clearTimeout(t);
  }, [isLanding]);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-parchment font-body text-ink antialiased">
        <AnimatePresence>{showIntro && <Intro />}</AnimatePresence>
        <div className="grain-overlay" aria-hidden="true" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/products/:categoryId" element={<CategoryPage />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
        <WhatsAppButton />
        <Toaster
          position="top-center"
          toastOptions={{
            style: { background: "#FD0119", color: "#FCFCFA", border: "1px solid rgba(252,252,250,0.35)" },
          }}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
